import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Search, RefreshCw, ShieldCheck, AlertCircle, Activity, Copy, ExternalLink } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface Payment {
  id: string;
  user_id: string | null;
  user_name: string | null;
  email: string;
  phone: string | null;
  final_price: number;
  currency: string | null;
  payment_status: string;
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
  paypal_order_id: string | null;
  payment_provider: string | null;
  created_at: string;
  scholarship_code: string | null;
  discount_applied: number | null;
}

interface AdminAction {
  id: string;
  action: string;
  admin_email: string | null;
  reason: string | null;
  before_status: string | null;
  after_status: string | null;
  metadata: any;
  created_at: string;
}

interface WebhookEvent {
  id: string;
  event_type: string | null;
  signature_valid: boolean;
  processed: boolean;
  error: string | null;
  created_at: string;
}

interface WebhookHealth {
  total24h: number;
  failed24h: number;
  lastEvent: WebhookEvent | null;
}

const statusBadge = (s: string) => {
  if (s === "success") return <Badge className="bg-green-600 hover:bg-green-600">success</Badge>;
  if (s === "pending") return <Badge variant="secondary">pending</Badge>;
  return <Badge variant="destructive">{s}</Badge>;
};

const AdminPaymentConsole = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Payment | null>(null);
  const [detailActions, setDetailActions] = useState<AdminAction[]>([]);
  const [detailWebhooks, setDetailWebhooks] = useState<WebhookEvent[]>([]);
  const [reason, setReason] = useState("");
  const [acting, setActing] = useState(false);
  const [rzpLookup, setRzpLookup] = useState<any>(null);
  const [webhookHealth, setWebhookHealth] = useState<WebhookHealth | null>(null);
  const [recentWebhooks, setRecentWebhooks] = useState<WebhookEvent[]>([]);

  const search = async (term?: string) => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("admin-payment-console", {
      body: { action: "search", query: term ?? q },
    });
    setLoading(false);
    if (error) {
      toast({ title: "Search failed", description: error.message, variant: "destructive" });
      return;
    }
    setResults(data?.results ?? []);
  };

  const loadWebhookHealth = async () => {
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data: all } = await supabase
      .from("razorpay_webhook_events")
      .select("id, event_type, signature_valid, processed, error, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false });
    const events = (all ?? []) as WebhookEvent[];
    setRecentWebhooks(events.slice(0, 10));
    setWebhookHealth({
      total24h: events.length,
      failed24h: events.filter((e) => !e.processed || !e.signature_valid).length,
      lastEvent: events[0] ?? null,
    });
  };

  useEffect(() => {
    search("");
    loadWebhookHealth();
  }, []);

  const openDetail = async (p: Payment) => {
    setSelected(p);
    setReason("");
    setRzpLookup(null);
    setDetailActions([]);
    setDetailWebhooks([]);
    const { data } = await supabase.functions.invoke("admin-payment-console", {
      body: { action: "detail", payment_id: p.id },
    });
    if (data?.payment) setSelected(data.payment);
    setDetailActions(data?.actions ?? []);
    setDetailWebhooks(data?.webhooks ?? []);
  };

  const doAction = async (action: string, extra: Record<string, any> = {}) => {
    if (!selected) return;
    setActing(true);
    const { data, error } = await supabase.functions.invoke("admin-payment-console", {
      body: { action, payment_id: selected.id, reason, ...extra },
    });
    setActing(false);
    if (error || data?.error) {
      toast({ title: "Action failed", description: error?.message || data?.error, variant: "destructive" });
      return;
    }
    if (action === "razorpay_lookup") {
      setRzpLookup(data);
      return;
    }
    toast({ title: "Done", description: action.replace("_", " ") });
    await openDetail(selected);
    await search();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-7xl mx-auto">
          <WeKitLogo size="sm" />
          <Badge variant="outline">Admin · Payment Console</Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Webhook setup */}
        <Card className="border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" /> Razorpay Webhook Setup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Paste this URL into <span className="font-medium">Razorpay Dashboard → Settings → Webhooks</span>.
              Subscribe to <code className="px-1 rounded bg-muted">payment.captured</code>,{" "}
              <code className="px-1 rounded bg-muted">payment.failed</code>, and{" "}
              <code className="px-1 rounded bg-muted">order.paid</code>. The webhook secret must match
              the <code className="px-1 rounded bg-muted">RAZORPAY_WEBHOOK_SECRET</code> stored in Lovable Cloud.
            </p>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value="https://ysqqubwfmtmtasgmfoim.supabase.co/functions/v1/razorpay-webhook"
                className="font-mono text-xs"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(
                    "https://ysqqubwfmtmtasgmfoim.supabase.co/functions/v1/razorpay-webhook"
                  );
                  toast({ title: "Copied", description: "Webhook URL copied to clipboard" });
                }}
              >
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
              </Button>
              <Button size="sm" variant="ghost" asChild>
                <a
                  href="https://dashboard.razorpay.com/app/webhooks"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Razorpay <ExternalLink className="h-3.5 w-3.5 ml-1" />
                </a>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              After saving, click <span className="font-medium">Test Webhook</span> in Razorpay — a row
              should appear in the health panel below within ~30 seconds. If signature is invalid, the
              secret is mismatched.
            </p>
          </CardContent>
        </Card>

        {/* Webhook health */}
        <Card className="border-border/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" /> Razorpay Webhook Health (24h)
              </CardTitle>
            </div>
            <Button size="sm" variant="ghost" onClick={loadWebhookHealth}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </CardHeader>
          <CardContent>
            {webhookHealth ? (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Events received</p>
                  <p className="text-2xl font-bold">{webhookHealth.total24h}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Failed / unprocessed</p>
                  <p className="text-2xl font-bold text-destructive">
                    {webhookHealth.failed24h}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Last event</p>
                  {webhookHealth.lastEvent ? (
                    <p className="text-sm">
                      {webhookHealth.lastEvent.event_type ?? "—"} ·{" "}
                      {new Date(webhookHealth.lastEvent.created_at).toLocaleString()} ·{" "}
                      {webhookHealth.lastEvent.processed ? (
                        <span className="text-green-600">processed</span>
                      ) : (
                        <span className="text-destructive">failed</span>
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" /> No webhook events received in last 24h
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Loading…</p>
            )}

            {recentWebhooks.length > 0 && (
              <div className="mt-4 border-t border-border/40 pt-3">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Last 10 webhook calls
                </p>
                <div className="space-y-1 text-xs font-mono">
                  {recentWebhooks.map((w) => (
                    <div key={w.id} className="flex items-center gap-2">
                      {w.processed && w.signature_valid ? (
                        <ShieldCheck className="h-3 w-3 text-green-600" />
                      ) : (
                        <AlertCircle className="h-3 w-3 text-destructive" />
                      )}
                      <span className="text-muted-foreground">
                        {new Date(w.created_at).toLocaleTimeString()}
                      </span>
                      <span>{w.event_type ?? "—"}</span>
                      {w.error && <span className="text-destructive">{w.error}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Search */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-base">Search payments</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => { e.preventDefault(); search(); }}
              className="flex gap-2"
            >
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="email, name, order_id, payment_id…"
                  className="pl-9"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={loading}>
                {loading ? "Searching…" : "Search"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((p) => (
                    <TableRow
                      key={p.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => openDetail(p)}
                    >
                      <TableCell className="font-medium">{p.user_name}</TableCell>
                      <TableCell className="text-muted-foreground">{p.email}</TableCell>
                      <TableCell>
                        {p.currency ?? "INR"} {p.final_price}
                      </TableCell>
                      <TableCell>{statusBadge(p.payment_status)}</TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {p.razorpay_order_id ?? p.paypal_order_id ?? "—"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(p.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {results.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No results
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selected?.user_name} · {selected?.email}
            </DialogTitle>
          </DialogHeader>

          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground">Status:</span> {statusBadge(selected.payment_status)}</div>
                <div><span className="text-muted-foreground">Amount:</span> {selected.currency ?? "INR"} {selected.final_price}</div>
                <div className="font-mono text-xs"><span className="text-muted-foreground font-sans">Order:</span> {selected.razorpay_order_id ?? selected.paypal_order_id ?? "—"}</div>
                <div className="font-mono text-xs"><span className="text-muted-foreground font-sans">Payment ID:</span> {selected.razorpay_payment_id ?? "—"}</div>
                <div><span className="text-muted-foreground">Provider:</span> {selected.payment_provider}</div>
                <div><span className="text-muted-foreground">Coupon:</span> {selected.scholarship_code ?? "—"}</div>
              </div>

              <Textarea
                placeholder="Reason / note (recorded in audit log)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[60px]"
              />

              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => doAction("razorpay_lookup")} disabled={acting || !selected.razorpay_order_id}>
                  Look up on Razorpay
                </Button>
                <Button size="sm" onClick={() => doAction("reconcile")} disabled={acting || selected.payment_status === "success" || !selected.razorpay_order_id}>
                  Reconcile from Razorpay
                </Button>
                <Button size="sm" variant="secondary" onClick={() => doAction("set_status", { after_status: "success" })} disabled={acting || selected.payment_status === "success"}>
                  Manually approve
                </Button>
                <Button size="sm" variant="destructive" onClick={() => doAction("set_status", { after_status: "failed" })} disabled={acting || selected.payment_status === "failed"}>
                  Mark failed
                </Button>
              </div>

              {rzpLookup && (
                <Card className="bg-muted/30 border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs">Razorpay order status</CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs font-mono space-y-1">
                    <div>Order: {rzpLookup.order?.status} · amount {rzpLookup.order?.amount_paid}/{rzpLookup.order?.amount}</div>
                    {rzpLookup.payments?.length === 0 && <div className="text-muted-foreground">No payments on this order</div>}
                    {rzpLookup.payments?.map((p: any) => (
                      <div key={p.id}>
                        {p.id} · {p.status} · {p.method} · ₹{Math.round(p.amount / 100).toLocaleString("en-IN")}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              <div>
                <p className="font-medium mb-1">Webhook events for this order</p>
                {detailWebhooks.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No webhook events received for this order.</p>
                ) : (
                  <div className="space-y-1 text-xs font-mono">
                    {detailWebhooks.map((w) => (
                      <div key={w.id} className="flex items-center gap-2">
                        {w.processed && w.signature_valid ? (
                          <ShieldCheck className="h-3 w-3 text-green-600" />
                        ) : (
                          <AlertCircle className="h-3 w-3 text-destructive" />
                        )}
                        <span className="text-muted-foreground">{new Date(w.created_at).toLocaleString()}</span>
                        <span>{w.event_type ?? "—"}</span>
                        {w.error && <span className="text-destructive">{w.error}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="font-medium mb-1">Admin action history</p>
                {detailActions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No admin actions yet.</p>
                ) : (
                  <div className="space-y-1 text-xs">
                    {detailActions.map((a) => (
                      <div key={a.id} className="border-l-2 border-primary/40 pl-2 py-1">
                        <div className="font-medium">{a.action} · {a.before_status} → {a.after_status}</div>
                        <div className="text-muted-foreground">
                          {a.admin_email} · {new Date(a.created_at).toLocaleString()}
                        </div>
                        {a.reason && <div className="italic">{a.reason}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPaymentConsole;
